import React, { useMemo, useCallback } from 'react';
import { Box, Modal as LegoModal, Form } from '@lucid/lego';
import { formatOffsetDateTime } from '../utils';
import { buildEngagementFormFields, buildClosedFormFields } from './formFields';
import styles from './Modal.module.scss';

/**
 * Payload for Contacting/Engaged status updates
 */
export type EngagementStatusPayload = {
  type: 'engagement';
  nextEngagementDate: string;
  notes: string;
};

/**
 * Payload for Closed status updates
 */
export type ClosedStatusPayload = {
  type: 'closed';
  subStatus: string;
  primaryCloseReason: string;
  secondaryCloseReason: string;
};

/**
 * Union type for all status update payloads
 */
export type StatusUpdatePayload = EngagementStatusPayload | ClosedStatusPayload;

type ModalProps = {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** The status being updated to */
  updatingToStatus: string;
  /** Initial next engagement date value (for Contacting/Engaged) */
  initialNextEngagementDate?: string;
  /** Theme for the modal */
  theme?: 'light' | 'dark';
  /** Handler for save button click */
  onSave: (data: StatusUpdatePayload) => void;
  /** Whether the form is submitting */
  isLoading?: boolean;
};

/**
 * Modal for updating prospect status
 * - For "Contacting" and "Engaged": shows Next Engagement Date + Notes fields
 * - For "Closed": shows Sub Status dropdown and conditionally Primary/Secondary Reason dropdowns
 * @component
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  updatingToStatus,
  initialNextEngagementDate = '',
  theme = 'light',
  onSave,
  isLoading = false,
}) => {
  // Determine if this is a "Closed" status update
  const isClosedStatus = updatingToStatus.toLowerCase() === 'closed';

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle form success for engagement status (Contacting/Engaged)
  const handleEngagementFormSuccess = useCallback(
    (data: unknown) => {
      const formData = data as { nextEngagementDate?: string; notes?: string };

      if (!formData.nextEngagementDate) {
        return;
      }

      // Parse the date string and create a Date object with current time
      const selectedDate = new Date(formData.nextEngagementDate);
      const currentDate = new Date();
      // Use the current time instead of hardcoded values
      selectedDate.setHours(
        currentDate.getHours(),
        currentDate.getMinutes(),
        currentDate.getSeconds(),
        0
      );
      const dateTime = formatOffsetDateTime(selectedDate);

      onSave({
        type: 'engagement',
        nextEngagementDate: dateTime,
        notes: formData.notes || '',
      });
      onClose();
    },
    [onSave, onClose]
  );

  // Form fields for engagement status (Contacting/Engaged)
  const engagementFormFields = useMemo(
    () =>
      buildEngagementFormFields(
        updatingToStatus,
        initialNextEngagementDate,
        handleCancel,
        isLoading
      ),
    [updatingToStatus, initialNextEngagementDate, handleCancel, isLoading]
  );

  // Handle form success for closed status
  const handleClosedFormSuccess = useCallback(
    (data: unknown) => {
      const formData = data as {
        subStatus?: string;
        primaryCloseReason?: string;
        secondaryCloseReason?: string;
      };

      const subStatus = formData.subStatus || '';

      // Sub statuses that require primary/secondary reasons
      const subStatusesWithReasons = [
        'junk',
        'not_interested',
        'not_qualified',
        'not_sales_inquiry',
      ];
      const requiresReasons = subStatusesWithReasons.includes(subStatus);

      // Only include primary/secondary reasons if the sub status requires them
      // This prevents sending stale values when switching from a sub status with reasons
      // to one without (like "Unresponsive")
      const payload: ClosedStatusPayload = {
        type: 'closed',
        subStatus,
        primaryCloseReason: requiresReasons ? formData.primaryCloseReason || '' : '',
        secondaryCloseReason: requiresReasons ? formData.secondaryCloseReason || '' : '',
      };

      onSave(payload);
      onClose();
    },
    [onSave, onClose]
  );

  // Form fields for closed status
  const closedFormFields = useMemo(
    () => buildClosedFormFields(handleCancel, isLoading),
    [handleCancel, isLoading]
  );

  // Render content for engagement status (Contacting/Engaged)
  const renderEngagementContent = () => (
    <Box className={styles.content}>
      <Form
        id="engagement-status-form"
        theme={theme}
        formFields={engagementFormFields}
        onSuccess={handleEngagementFormSuccess}
        isFluid={true}
      />
    </Box>
  );

  // Render content for closed status
  const renderClosedContent = () => (
    <Box className={styles.content}>
      <Form
        id="closed-status-form"
        theme={theme}
        formFields={closedFormFields}
        onSuccess={handleClosedFormSuccess}
        isFluid={true}
      />
    </Box>
  );

  return (
    <LegoModal
      dataTestId="prospect-details-modal"
      isOpen={isOpen}
      onClose={handleCancel}
      title="Confirm Action"
      theme={theme}
      size="small"
      className={styles.modal}
    >
      {isClosedStatus ? renderClosedContent() : renderEngagementContent()}
    </LegoModal>
  );
};

Modal.displayName = 'Modal';

export default Modal;
